import React, { useState, useRef, useEffect } from 'react';
import { Handle, NodeResizer } from 'reactflow';
import { Edit, Trash2 } from 'lucide-react';
import { NODE_MIN_WIDTH, NODE_MIN_HEIGHT } from '../constants';

/**
 * Condition node — branches the flow into a Yes and a No path.
 *
 * The engine evaluates data.variable / data.operator / data.value against the
 * execution context and follows edges whose sourceHandle is 'yes' or 'no'.
 * The two source handles at the bottom map to those handles.
 */
const ConditionNode = ({ data, id, selected }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        if (event.target.closest('button')?.textContent?.includes('⋮')) return;
        setShowMenu(false);
      }
    };

    if (showMenu) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const dispatchDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('deleteNode', { detail: { nodeId: id } }));
  };

  const conditionSummary = data.variable
    ? `If ${data.variable} ${(data.operator || 'equals').replace(/_/g, ' ')}${data.value ? ` "${data.value}"` : ''}`
    : 'No condition configured';

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <NodeResizer
        color="#667eea"
        isVisible={selected}
        minWidth={NODE_MIN_WIDTH}
        minHeight={NODE_MIN_HEIGHT}
      />
      <Handle type="target" position="top" style={{ background: '#cbd5e0' }} />
      <Handle type="target" position="left" id="left-target" style={{ background: '#cbd5e0' }} />
      <div style={{
        background: 'white', borderRadius: '8px',
        border: selected ? '2px solid #667eea' : '1px solid #e2e8f0',
        overflow: 'visible', cursor: 'pointer', position: 'relative',
        width: '100%', height: '100%', boxSizing: 'border-box',
      }}>
        <div style={{
          padding: '12px 16px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', position: 'relative',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '24px', height: '24px', borderRadius: '4px', background: '#fefcbf',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px',
            }}>🔀</div>
            <span style={{ fontWeight: '600', fontSize: '14px' }}>{data.label}</span>
          </div>
          <div className="nodrag" style={{ display: 'flex', alignItems: 'center', gap: '4px', pointerEvents: 'auto', position: 'relative', zIndex: 10 }}>
            <button
              type="button"
              className="nodrag"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setShowMenu((p) => !p); }}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: '16px', color: '#a0aec0', padding: '4px 8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'auto', position: 'relative', zIndex: 10,
              }}
            >⋮</button>
            <button
              type="button"
              className="nodrag"
              onPointerDown={dispatchDelete}
              onMouseDown={dispatchDelete}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#ef4444', padding: '4px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'auto', position: 'relative', zIndex: 10,
              }}
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        <div style={{ padding: '12px 16px', fontSize: '12px', color: '#718096' }}>
          {conditionSummary}
        </div>
        {data.subtitle ? (
          <div style={{ padding: '0 16px 12px', fontSize: '11px', color: '#a0aec0', lineHeight: 1.45 }}>
            {data.subtitle}
          </div>
        ) : null}

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          padding: '0 24px 8px', fontSize: '11px', fontWeight: '600',
        }}>
          <span style={{ color: '#10b981' }}>Yes</span>
          <span style={{ color: '#ef4444' }}>No</span>
        </div>
      </div>

      {showMenu && (
        <div ref={menuRef} style={{
          position: 'absolute', top: '50px', right: '45px',
          background: 'white', border: '1px solid #e2e8f0',
          borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999, minWidth: '140px',
        }}>
          <button
            className="nodrag"
            onClick={(e) => {
              e.stopPropagation(); e.preventDefault();
              setShowMenu(false);
              window.dispatchEvent(new CustomEvent('editNode', { detail: { nodeId: id } }));
            }}
            style={{
              width: '100%', padding: '10px 12px', background: 'transparent',
              border: 'none', cursor: 'pointer', fontSize: '13px', color: '#4a5568',
              display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f7fafc')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Edit size={14} /> Edit Node
          </button>
        </div>
      )}

      <Handle
        type="source"
        position="bottom"
        id="yes"
        style={{ background: '#10b981', left: '25%' }}
      />
      <Handle
        type="source"
        position="bottom"
        id="no"
        style={{ background: '#ef4444', left: '75%' }}
      />
    </div>
  );
};

export default ConditionNode;
